import { CURRENCIES } from '../constants';

export function currencySymbol(code = 'USD') {
  return CURRENCIES.find((c) => c.value === code)?.symbol ?? '$';
}

export function formatCurrency(amount, currency = 'USD') {
  if (amount == null || isNaN(amount)) return '—';
  const sym = currencySymbol(currency);
  const abs = Math.abs(Number(amount));
  const formatted =
    abs >= 1_000_000
      ? `${(abs / 1_000_000).toFixed(1)}M`
      : abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return `${Number(amount) < 0 ? '-' : ''}${sym}${formatted}`;
}

export function pctChange(oldVal, newVal) {
  if (!oldVal || oldVal === 0) return newVal > 0 ? '+∞' : '0%';
  const pct = ((newVal - oldVal) / oldVal) * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

export function changeAmount(oldVal, newVal, currency = 'USD') {
  const diff = (newVal || 0) - (oldVal || 0);
  const sign = diff > 0 ? '+' : '';
  return `${sign}${formatCurrency(diff, currency)}`;
}

export function nameFromEmail(email = '') {
  return email
    .split('@')[0]
    .replace(/[._-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function initials(name = '') {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function calendarDate(year, month, day) {
  const y = year < 100 ? 2000 + year : year;
  const d = new Date(y, month - 1, day);
  return d.getFullYear() === y && d.getMonth() === month - 1 && d.getDate() === day ? d : null;
}

// Explicit formats only, read as local calendar days: new Date('2026-10-01') is UTC midnight, which
// shows as Sep 30 west of UTC, and browsers disagree on two-digit years like '10/1/26' (US order).
function parseDateStrict(s) {
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return calendarDate(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (m) return calendarDate(+m[3], +m[1], +m[2]);
  m = s.match(/^([a-z]{3,9})\.?\s+(\d{1,2}),?\s+(\d{4})$/i);
  if (m && MONTHS.includes(m[1].slice(0, 3).toLowerCase())) {
    return calendarDate(+m[3], MONTHS.indexOf(m[1].slice(0, 3).toLowerCase()) + 1, +m[2]);
  }
  return undefined;
}

export function parseDate(val) {
  if (!val) return null;
  if (val.toDate) return val.toDate();
  const s = String(val).trim();
  const strict = parseDateStrict(s);
  if (strict !== undefined) return strict;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(val) {
  const d = parseDate(val);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

// A pay date that starts with a date ("Oct 30, 2026 paycheck") completes "…reflected in your ___".
const LEADING_DATE = /^((?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+(?:\d{1,2}(?:,?\s+\d{4})?|\d{4})|\d{1,2}\/\d{1,2}(?:\/\d{2}(?:\d{2})?)?|\d{4}-\d{1,2}-\d{1,2})(?=[\s,.]|$)/i;

function paycheckPhrase(payDate) {
  const s = String(payDate ?? '').trim().replace(/\.+$/, '').replace(/^your\s+/i, '');
  const m = s.match(LEADING_DATE);
  if (!m) return null;
  const when = parseDateStrict(m[1]) ? formatDate(m[1]) : m[1];
  return `${when}${s.slice(m[1].length) || ' paycheck'}`;
}

/** The statement's timing sentences, e.g. "These changes will be effective Oct 1, 2026 and will be reflected in your Oct 30, 2026 paycheck." */
export function statementTiming(effectiveDate, payDate) {
  const effective = effectiveDate ? (parseDate(effectiveDate) ? formatDate(effectiveDate) : String(effectiveDate).trim()) : null;
  const paycheck = paycheckPhrase(payDate);
  const other = !paycheck && String(payDate ?? '').trim() ? `${String(payDate).trim().replace(/\.+$/, '')}.` : null;
  const lead = effective && paycheck ? `These changes will be effective ${effective} and will be reflected in your ${paycheck}.`
    : effective ? `These changes will be effective ${effective}.`
      : paycheck ? `These changes will be reflected in your ${paycheck}.`
        : null;
  return [lead, other].filter(Boolean);
}
